import { QueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client.js';

const RETRYABLE_STATUS = new Set([408, 429, 500, 502, 503, 504]);

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (failureCount >= 2) return false;
          if (error instanceof ApiError) return RETRYABLE_STATUS.has(error.status);
          return true;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export const queryKeys = {
  session: ['session'] as const,
  boards: {
    all: ['boards'] as const,
    list: (includeInactive: boolean) => ['boards', 'list', includeInactive] as const,
    detail: (boardId: string) => ['boards', 'detail', boardId] as const,
    cards: (boardId: string) => ['boards', 'cards', boardId] as const,
    archived: (boardId: string) => ['boards', 'archived', boardId] as const,
  },
  tickets: {
    all: ['tickets'] as const,
    list: (filters: Record<string, unknown>) => ['tickets', 'list', filters] as const,
    detail: (id: string) => ['tickets', 'detail', id] as const,
    comments: (id: string) => ['tickets', 'comments', id] as const,
    events: (id: string) => ['tickets', 'events', id] as const,
    attachments: (id: string) => ['tickets', 'attachments', id] as const,
  },
  users: {
    all: ['users'] as const,
    lists: ['users', 'list'] as const,
    list: (filters: Record<string, unknown>) => ['users', 'list', filters] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
  },
  org: {
    departments: {
      all: ['org', 'departments'] as const,
      list: (includeInactive: boolean) => ['org', 'departments', includeInactive] as const,
    },
    jobRoles: {
      all: ['org', 'job-roles'] as const,
      list: (includeInactive: boolean) => ['org', 'job-roles', includeInactive] as const,
    },
    projects: ['org', 'projects'] as const,
    users: ['org', 'users'] as const,
    assignable: (departmentId: string) => ['org', 'assignable', departmentId] as const,
  },
  reports: {
    dashboard: (days: number) => ['reports', 'dashboard', days] as const,
  },
} as const;
